// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../utils/useAuth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

export default function Navbar() {
  const { user, profile, effectiveRole, switchRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [gamesDropdownOpen, setGamesDropdownOpen] = useState(false);
  const [refGamesDropdownOpen, setRefGamesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileGamesOpen, setMobileGamesOpen] = useState(true);

  const isAdmin = profile?.role === "admin";
  const activeRole = effectiveRole || profile?.role;

  // Listen for unread notifications (for managers/admins/coaches/referees)
  useEffect(() => {
    if (!user || (activeRole !== "manager" && activeRole !== "admin" && activeRole !== "coach" && activeRole !== "referee")) {
      setUnreadCount(0);
      return;
    }

    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", user.uid),
      where("read", "==", false)
    );

    const unsub = onSnapshot(q, (snap) => {
      setUnreadCount(snap.docs.length);
    });

    return unsub;
  }, [user, activeRole]);

  // Don't render until user and profile are loaded
  if (!user || !profile) return null;

  // Helper function to determine if link is active
  const isActive = (path) => location.pathname === path;
  const linkClass = (path) => isActive(path) ? "text-green-500 font-semibold" : "hover:text-green-400";

  async function handleRoleChange(e) {
    const newRole = e.target.value;
    await switchRole(newRole);

    switch (newRole) {
      case "admin":
        navigate("/admin/users");
        break;
      case "manager":
        navigate("/manager");
        break;
      case "coach":
        navigate("/coach");
        break;
      case "referee":
        navigate("/ref");
        break;
      default:
        navigate("/");
    }
  }

  return (
    <nav className="bg-gray-900 text-white">
      {/* Mobile/Desktop Header */}
      <div className="px-4 md:px-6 py-3 flex justify-between items-center">
        <div className="text-xl font-bold">
          <Link to="/">
            <span className="text-white">Ref</span>
            <span className="text-green-500">Call</span>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 hover:bg-gray-800 rounded"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-6">
        {activeRole === "admin" && <Link to="/admin/users" className={linkClass("/admin/users")}>Admin</Link>}
        {activeRole === "manager" && (
          <>
            <div className="relative">
              <button
                onClick={() => setGamesDropdownOpen(!gamesDropdownOpen)}
                className={`${isActive("/manager") || isActive("/manager/add-game") || isActive("/manager/calendar") ? "text-green-500 font-semibold" : "hover:text-green-400"} flex items-center space-x-1`}
              >
                <span>Games</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {gamesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-gray-800 rounded shadow-lg py-2 z-50">
                  <Link
                    to="/manager"
                    onClick={() => setGamesDropdownOpen(false)}
                    className="block px-4 py-2 hover:bg-gray-700"
                  >
                    Games Dashboard
                  </Link>
                  <Link
                    to="/manager/add-game"
                    onClick={() => setGamesDropdownOpen(false)}
                    className="block px-4 py-2 hover:bg-gray-700"
                  >
                    Games Creator
                  </Link>
                  <Link
                    to="/manager/calendar"
                    onClick={() => setGamesDropdownOpen(false)}
                    className="block px-4 py-2 hover:bg-gray-700"
                  >
                    Calendar View
                  </Link>
                </div>
              )}
            </div>
            <Link to="/manager/referees" className={linkClass("/manager/referees")}>Referees</Link>
            <Link to="/manager/approvals" className={linkClass("/manager/approvals")}>Approvals</Link>
            <Link to="/manager/competitions" className={linkClass("/manager/competitions")}>Competitions</Link>
            <Link to="/manager/billing" className={linkClass("/manager/billing")}>Billing</Link>
            <Link to="/manager/notifications" className={`${linkClass("/manager/notifications")} relative`}>
              Notifications
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {unreadCount}
                </span>
              )}
            </Link>
          </>
        )}
        {activeRole === "coach" && (
          <>
            <Link to="/coach" className={linkClass("/coach")}>Games</Link>
            <Link to="/add-game" className={linkClass("/add-game")}>New Game Request</Link>
            <Link to="/manage-fields" className={linkClass("/manage-fields")}>Field Management</Link>
            <Link to="/competition-request" className={linkClass("/competition-request")}>Competition Request</Link>
            <Link to="/coach/notifications" className={`${linkClass("/coach/notifications")} relative`}>
              Notifications
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {unreadCount}
                </span>
              )}
            </Link>
          </>
        )}
        {activeRole === "referee" && (
          <>
            <div className="relative">
              <button
                onClick={() => setRefGamesDropdownOpen(!refGamesDropdownOpen)}
                className={`${isActive("/ref") || isActive("/ref/calendar") ? "text-green-500 font-semibold" : "hover:text-green-400"} flex items-center space-x-1`}
              >
                <span>Games</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {refGamesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-gray-800 rounded shadow-lg py-2 z-50">
                  <Link
                    to="/ref"
                    onClick={() => setRefGamesDropdownOpen(false)}
                    className="block px-4 py-2 hover:bg-gray-700"
                  >
                    Games Dashboard
                  </Link>
                  <Link
                    to="/ref/calendar"
                    onClick={() => setRefGamesDropdownOpen(false)}
                    className="block px-4 py-2 hover:bg-gray-700"
                  >
                    Calendar View
                  </Link>
                </div>
              )}
            </div>
            <Link to="/ref/availability" className={linkClass("/ref/availability")}>My Availability</Link>
            <Link to="/ref/match-fees" className={linkClass("/ref/match-fees")}>Match Fees</Link>
              <Link to="/ref/notifications" className={`${linkClass("/ref/notifications")} relative`}>
                Notifications
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </Link>
            </>
          )
        }

        <Link to="/profile" className={linkClass("/profile")}>My Profile</Link>

        {isAdmin ? (
          <select
            value={activeRole}
            onChange={handleRoleChange}
            className="text-black px-3 py-1 rounded"
          >
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="coach">Coach</option>
            <option value="referee">Referee</option>
          </select>
        ) : (
          <span className="px-2 py-1 bg-gray-700 rounded text-sm">
            {profile.role.toUpperCase()}
          </span>
        )}

        <button
          onClick={logout}
          className="bg-red-600 px-4 py-1 rounded hover:bg-red-700"
        >
          Logout
        </button>
      </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-800 border-t border-gray-700 px-4 py-3 space-y-3">
          {activeRole === "admin" && (
            <Link to="/admin/users" onClick={() => setMobileMenuOpen(false)} className="block py-2 hover:text-green-400">
              Admin
            </Link>
          )}
          
          {activeRole === "manager" && (
            <>
              <button
                onClick={() => setMobileGamesOpen(!mobileGamesOpen)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-300 mt-2 mb-2 hover:text-green-400"
              >
                <span>Games</span>
                <svg
                  className={`w-4 h-4 transition-transform ${mobileGamesOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileGamesOpen && (
                <>
                  <Link to="/manager" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    Dashboard
                  </Link>
                  <Link to="/manager/add-game" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    Games Creator
                  </Link>
                  <Link to="/manager/calendar" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    Calendar View
                  </Link>
                </>
              )}
              
              <Link to="/manager/notifications" onClick={() => setMobileMenuOpen(false)} className="block py-3 mt-4 hover:text-green-400 hover:bg-gray-700 rounded relative">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 inline-flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/manager/referees" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Referees
              </Link>
              <Link to="/manager/approvals" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Approvals
              </Link>
              <Link to="/manager/competitions" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Competitions
              </Link>
              <Link to="/manager/billing" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Billing
              </Link>
              <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Profile
              </Link>
            </>
          )}

          {activeRole === "coach" && (
            <>
              <button
                onClick={() => setMobileGamesOpen(!mobileGamesOpen)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-300 mt-2 mb-2 hover:text-green-400"
              >
                <span>Games</span>
                <svg
                  className={`w-4 h-4 transition-transform ${mobileGamesOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileGamesOpen && (
                <>
                  <Link to="/coach" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    My Games
                  </Link>
                  <Link to="/add-game" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    New Game Request
                  </Link>
                </>
              )}
              
              <Link to="/coach/notifications" onClick={() => setMobileMenuOpen(false)} className="block py-3 mt-4 hover:text-green-400 hover:bg-gray-700 rounded relative">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 inline-flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/manage-fields" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Field Management
              </Link>
              <Link to="/competition-request" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Competition Request
              </Link>
              <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Profile
              </Link>
            </>
          )}

          {activeRole === "referee" && (
            <>
              <button
                onClick={() => setMobileGamesOpen(!mobileGamesOpen)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-300 mt-2 mb-2 hover:text-green-400"
              >
                <span>Games</span>
                <svg
                  className={`w-4 h-4 transition-transform ${mobileGamesOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {mobileGamesOpen && (
                <>
                  <Link to="/ref" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    Dashboard
                  </Link>
                  <Link to="/ref/calendar" onClick={() => setMobileMenuOpen(false)} className="block py-3 pl-4 hover:text-green-400 hover:bg-gray-700 rounded">
                    Calendar View
                  </Link>
                </>
              )}
              
              <Link to="/ref/notifications" onClick={() => setMobileMenuOpen(false)} className="block py-3 mt-4 hover:text-green-400 hover:bg-gray-700 rounded relative">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 inline-flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/ref/availability" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Availability
              </Link>
              <Link to="/ref/match-fees" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Match Fees
              </Link>
              <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block py-3 hover:text-green-400 hover:bg-gray-700 rounded">
                Profile
              </Link>
            </>
          )}

          {isAdmin && (
            <div className="pt-4 border-t border-gray-700 mt-4">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Switch Role</label>
              <select
                value={activeRole}
                onChange={(e) => {
                  handleRoleChange(e);
                  setMobileMenuOpen(false);
                }}
                className="w-full text-black px-3 py-2 rounded"
              >
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="coach">Coach</option>
                <option value="referee">Referee</option>
              </select>
            </div>
          )}

          <button
            onClick={() => {
              logout();
              setMobileMenuOpen(false);
            }}
            className="w-full mt-4 py-3 bg-red-600 text-white rounded hover:bg-red-700 font-semibold"
          >
            Logout
          </button>
        </div>
      )}
    </nav>
  );
}
